import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  XMarkIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  HomeIcon,
  BriefcaseIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isUserDetailModalOpen, setIsUserDetailModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [isAuditScoreModalOpen, setIsAuditScoreModalOpen] = useState(false);
  const [auditScoreData, setAuditScoreData] = useState({
    auditScore: ''
  });
  const [pricingData, setPricingData] = useState({
    monthlyPrice: '',
    planName: '',
    notes: ''
  });
  const [expandedIntakeSections, setExpandedIntakeSections] = useState({});

  useEffect(() => {
    fetchUsers();
  }, [sortField, sortDirection]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/users', {
        params: {
          sort: sortField,
          direction: sortDirection,
          search: searchTerm,
        },
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // Implement debounced search
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleSetPricing = (user) => {
    setSelectedUser(user);
    setPricingData({
      monthlyPrice: user.monthlyPrice || '',
      planName: user.planName || '',
      notes: ''
    });
    setIsPricingModalOpen(true);
  };

  const handleSetAuditScore = (user) => {
    setSelectedUser(user);
    setAuditScoreData({
      auditScore: user.adminAuditScore || user.auditScore || ''
    });
    setIsAuditScoreModalOpen(true);
  };

  const handleSavePricing = async () => {
    if (!pricingData.monthlyPrice || !pricingData.planName) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.put(`/api/admin/client/${selectedUser.id}/pricing`, {
        monthlyPrice: parseFloat(pricingData.monthlyPrice),
        planName: pricingData.planName,
        notes: pricingData.notes,
        subscriptionStatus: 'review_complete'
      });
      
      toast.success('Pricing updated successfully');
      setIsPricingModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error('Failed to update pricing');
    }
  };

  const handleSaveAuditScore = async () => {
    if (auditScoreData.auditScore === '') {
      toast.error('Please enter an audit score');
      return;
    }

    const score = parseFloat(auditScoreData.auditScore);
    if (isNaN(score) || score < 0 || score > 100) {
      toast.error('Audit score must be a number between 0 and 100');
      return;
    }

    try {
      await api.put(`/api/admin/user/${selectedUser.id}/audit-score`, {
        auditScore: score
      });
      
      toast.success('Audit score updated successfully');
      setIsAuditScoreModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating audit score:', error);
      toast.error('Failed to update audit score');
    }
  };

  const handleViewUserDetails = async (userId) => {
    try {
      const response = await api.get(`/api/admin/users/${userId}`);
      if (response.data.success) {
        setUserDetails(response.data.user);
        setIsUserDetailModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to fetch user details');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/api/admin/users/${userId}`);
        fetchUsers();
        toast.success('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const SortIcon = ({ field }) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpIcon className="w-4 h-4" />
    ) : (
      <ArrowDownIcon className="w-4 h-4" />
    );
  };

  const toggleIntakeSection = (section) => {
    setExpandedIntakeSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatIntakeData = (intakeData) => {
    if (!intakeData) return null;

    const sections = {
      personal: {
        title: 'Personal Information',
        icon: UserIcon,
        color: 'blue',
        fields: [
          { key: 'firstName', label: 'First Name' },
          { key: 'lastName', label: 'Last Name' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'personalEmail', label: 'Personal Email' },
          { key: 'businessAddress', label: 'Business Address' },
          { key: 'city', label: 'City' },
          { key: 'state', label: 'State' },
          { key: 'zipCode', label: 'ZIP Code' },
          { key: 'website', label: 'Website' },
          { key: 'socialSecurityNumber', label: 'SSN' },
          { key: 'emergencyContactName', label: 'Emergency Contact' },
          { key: 'emergencyContactPhone', label: 'Emergency Phone' }
        ]
      },
      business: {
        title: 'Business Information',
        icon: BriefcaseIcon,
        color: 'green',
        fields: [
          { key: 'companyName', label: 'Company Name' },
          { key: 'businessType', label: 'Business Type' },
          { key: 'businessTypes', label: 'Business Types' },
          { key: 'yearsInBusiness', label: 'Years in Business' },
          { key: 'numberOfLocations', label: 'Number of Locations' },
          { key: 'employeeCount', label: 'Employee Count' },
          { key: 'businessDescription', label: 'Business Description' },
          { key: 'federalTaxId', label: 'Federal Tax ID' },
          { key: 'stateTaxId', label: 'State Tax ID' }
        ]
      },
      financial: {
        title: 'Financial Information',
        icon: BanknotesIcon,
        color: 'yellow',
        fields: [
          { key: 'annualRevenue', label: 'Annual Revenue' },
          { key: 'monthlyIncome', label: 'Monthly Income' },
          { key: 'taxPreparationMethod', label: 'Tax Preparation Method' },
          { key: 'hasAccountant', label: 'Has Accountant' },
          { key: 'accountantName', label: 'Accountant Name' },
          { key: 'accountantPhone', label: 'Accountant Phone' },
          { key: 'accountantEmail', label: 'Accountant Email' },
          { key: 'quarterlyEstimatedPayments', label: 'Quarterly Payments' },
          { key: 'previousYearRevenue', label: 'Previous Year Revenue' }
        ]
      },
      risk: {
        title: 'Risk Assessment',
        icon: ExclamationTriangleIcon,
        color: 'red',
        fields: [
          { key: 'industryType', label: 'Industry Type' },
          { key: 'mainConcern', label: 'Main Concern' },
          { key: 'hasDigitalAssets', label: 'Has Digital Assets' },
          { key: 'hasPreviousAudit', label: 'Previous Audit' },
          { key: 'previousAuditYear', label: 'Previous Audit Year' },
          { key: 'previousAuditOutcome', label: 'Previous Audit Outcome' },
          { key: 'hasForeignAssets', label: 'Has Foreign Assets' },
          { key: 'hasCryptocurrency', label: 'Has Cryptocurrency' },
          { key: 'hasRealEstate', label: 'Has Real Estate' },
          { key: 'hasBusinessPartners', label: 'Has Business Partners' }
        ]
      },
      additional: {
        title: 'Additional Information',
        icon: InformationCircleIcon,
        color: 'purple',
        fields: [
          { key: 'referralCode', label: 'Referral Code' },
          { key: 'additionalNotes', label: 'Additional Notes' },
          { key: 'howDidYouHear', label: 'How Did You Hear' },
          { key: 'preferredContactMethod', label: 'Preferred Contact' },
          { key: 'marketingConsent', label: 'Marketing Consent' },
          { key: 'privacyConsent', label: 'Privacy Consent' }
        ]
      }
    };

    return sections;
  };

  const formatValue = (value, field) => {
    if (value === null || value === undefined || value === '') return 'Not provided';
    
    if (field === 'businessTypes' && Array.isArray(value)) {
      return value.join(', ');
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (field === 'annualRevenue' || field === 'monthlyIncome' || field === 'previousYearRevenue') {
      return `$${Number(value).toLocaleString()}`;
    }
    
    return String(value);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
      {/* Header section */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
        <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
          Add New User
        </button>
      </div>

      {/* Search and filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Users table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('businessName')}
              >
                <div className="flex items-center gap-2">
                  Business Name
                  <SortIcon field="businessName" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center gap-2">
                  Email
                  <SortIcon field="email" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('planName')}
              >
                <div className="flex items-center gap-2">
                  Plan
                  <SortIcon field="planName" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('monthlyPrice')}
              >
                <div className="flex items-center gap-2">
                  Price
                  <SortIcon field="monthlyPrice" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('subscriptionStatus')}
              >
                <div className="flex items-center gap-2">
                  Status
                  <SortIcon field="subscriptionStatus" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('auditScore')}
              >
                <div className="flex items-center gap-2">
                  Audit Score
                  <SortIcon field="auditScore" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center gap-2">
                  Join Date
                  <SortIcon field="createdAt" />
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {user.businessName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.planName || 'TBD'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {user.monthlyPrice ? `$${user.monthlyPrice}` : 'TBD'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.subscriptionStatus === 'active'
                          ? 'bg-green-100 text-green-800'
                          : user.subscriptionStatus === 'pending_review'
                          ? 'bg-amber-100 text-amber-800'
                          : user.subscriptionStatus === 'review_complete'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {user.subscriptionStatus === 'pending_review' ? 'Account in Review' :
                       user.subscriptionStatus === 'review_complete' ? 'Ready to Pay' :
                       user.subscriptionStatus || 'Pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm font-medium ${
                        (user.adminAuditScore || user.auditScore || 0) > 70 ? 'text-red-600' :
                        (user.adminAuditScore || user.auditScore || 0) > 40 ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {user.adminAuditScore !== null ? user.adminAuditScore : user.auditScore || 'N/A'}
                      </span>
                      {user.adminAuditScore !== null && (
                        <span className="text-xs text-gray-500">(Admin)</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      {user.subscriptionStatus === 'pending_review' && (
                        <button
                          onClick={() => handleSetPricing(user)}
                          className="text-green-600 hover:text-green-900"
                          title="Set Pricing"
                        >
                          <CurrencyDollarIcon className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleSetAuditScore(user)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Set Audit Score"
                      >
                        <ChartBarIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit User"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleViewUserDetails(user.id)}
                        className="text-gray-600 hover:text-gray-900" 
                        title="View Details"
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete User"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-700">
          Showing <span className="font-medium">1</span> to{' '}
          <span className="font-medium">10</span> of{' '}
          <span className="font-medium">{users.length}</span> results
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            Previous
          </button>
          <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            Next
          </button>
        </div>
      </div>

      {/* Pricing Modal */}
      {isPricingModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Set Pricing for {selectedUser?.businessName}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name
                </label>
                <select
                  value={pricingData.planName}
                  onChange={(e) => setPricingData({...pricingData, planName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select Plan</option>
                  <option value="Basic">Basic Plan</option>
                  <option value="Professional">Professional Plan</option>
                  <option value="Enterprise">Enterprise Plan</option>
                  <option value="Custom">Custom Plan</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Price ($)
                </label>
                <input
                  type="number"
                  min="99"
                  max="999"
                  step="1"
                  value={pricingData.monthlyPrice}
                  onChange={(e) => setPricingData({...pricingData, monthlyPrice: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter monthly price"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={pricingData.notes}
                  onChange={(e) => setPricingData({...pricingData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows="3"
                  placeholder="Add any notes about this pricing decision..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setIsPricingModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePricing}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Save Pricing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {isUserDetailModalOpen && userDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                User Details - {userDetails.businessName}
              </h3>
              <button
                onClick={() => setIsUserDetailModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Business Name</p>
                      <p className="text-sm text-gray-600">{userDetails.businessName}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email</p>
                      <p className="text-sm text-gray-600">{userDetails.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Phone</p>
                      <p className="text-sm text-gray-600">{userDetails.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CalendarIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Joined</p>
                      <p className="text-sm text-gray-600">{new Date(userDetails.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscription Information */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 border-b pb-2">Subscription Details</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CurrencyDollarIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Plan</p>
                      <p className="text-sm text-gray-600">{userDetails.planName || 'Not Set'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CurrencyDollarIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Monthly Price</p>
                      <p className="text-sm text-gray-600">{userDetails.monthlyPrice ? `$${userDetails.monthlyPrice}` : 'Not Set'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <ClockIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Status</p>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        userDetails.subscriptionStatus === 'active' ? 'bg-green-100 text-green-800' :
                        userDetails.subscriptionStatus === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
                        userDetails.subscriptionStatus === 'review_complete' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {userDetails.subscriptionStatus === 'pending_review' ? 'Account in Review' :
                         userDetails.subscriptionStatus === 'review_complete' ? 'Ready to Pay' :
                         userDetails.subscriptionStatus || 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            {userDetails.documents && userDetails.documents.length > 0 && (
              <div className="mt-6">
                <h4 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4">Uploaded Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userDetails.documents.map((doc) => (
                    <div key={doc.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                          <p className="text-xs text-gray-600">{doc.formType} • {new Date(doc.uploadedAt).toLocaleDateString()}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          doc.verified ? 'bg-green-100 text-green-800' :
                          doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {doc.verified ? 'Verified' : doc.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Intake Data */}
            {userDetails.intakeData && Object.keys(userDetails.intakeData).length > 0 && (
              <div className="mt-6">
                <h4 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4">Intake Form Data</h4>
                <div className="space-y-4">
                  {Object.entries(formatIntakeData(userDetails.intakeData)).map(([sectionKey, section]) => {
                    const IconComponent = section.icon;
                    const isExpanded = expandedIntakeSections[sectionKey];
                    const colorClasses = {
                      blue: 'bg-blue-50 border-blue-200 text-blue-700',
                      green: 'bg-green-50 border-green-200 text-green-700',
                      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
                      red: 'bg-red-50 border-red-200 text-red-700',
                      purple: 'bg-purple-50 border-purple-200 text-purple-700'
                    };

                    return (
                      <div key={sectionKey} className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleIntakeSection(sectionKey)}
                          className={`w-full px-4 py-3 flex items-center justify-between ${colorClasses[section.color]} hover:opacity-80 transition-opacity`}
                        >
                          <div className="flex items-center space-x-3">
                            <IconComponent className="w-5 h-5" />
                            <span className="font-medium">{section.title}</span>
                          </div>
                          {isExpanded ? (
                            <ChevronDownIcon className="w-5 h-5" />
                          ) : (
                            <ChevronRightIcon className="w-5 h-5" />
                          )}
                        </button>
                        
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="bg-white"
                          >
                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                              {section.fields.map((field) => {
                                const value = userDetails.intakeData[field.key];
                                const formattedValue = formatValue(value, field.key);
                                
                                return (
                                  <div key={field.key} className="space-y-1">
                                    <dt className="text-sm font-medium text-gray-500">{field.label}</dt>
                                    <dd className={`text-sm ${
                                      formattedValue === 'Not provided' 
                                        ? 'text-gray-400 italic' 
                                        : 'text-gray-900'
                                    }`}>
                                      {formattedValue}
                                    </dd>
                                  </div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit Score Modal */}
      {isAuditScoreModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Set Audit Score for {selectedUser?.businessName}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Audit Score (0-100)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={auditScoreData.auditScore}
                  onChange={(e) => setAuditScoreData({...auditScoreData, auditScore: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter audit score"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Current calculated score: {selectedUser?.auditScore || 'N/A'}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setIsAuditScoreModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAuditScore}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Save Audit Score
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default Users; 